import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Tooltip } from "@mui/material";
import { TranslateRounded } from "@mui/icons-material";
import {
  documentCraftingFileUrls,
  downloadDocumentCraftingTranslationPdf,
} from "../../../Api/documentCrafting";
import { downloadDocumentBlob } from "../../../Helpers/documentHelper";
import {
  DocumentCraftingLanguage,
  DocumentCraftingRevisionTranslation,
  DocumentCraftingRunStatus,
  DocumentCraftingTranslationStatus,
  isRunTerminal,
} from "../../../Models/documentCrafting";
import {
  useDocumentCraftingRun,
  useRequestDocumentCraftingTranslation,
} from "../../../Query/useDocumentCrafting";
import { downloadFileName } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import PdfViewerDialog from "./PdfViewerDialog";

interface Params {
  documentId: number;
  title: string;
  revisionId: number;
  revisionNumber: number | null;
  /** The language to translate into (the other one). */
  language: DocumentCraftingLanguage;
  /** `RevisionSummary.translations` of the revision. */
  translations: DocumentCraftingRevisionTranslation[];
}

interface State {
  revisionId: number;
  runId: number | null;
  open: boolean;
  error: string | null;
}

const idle = (revisionId: number): State => ({
  revisionId,
  runId: null,
  open: false,
  error: null,
});

/**
 * The translation preview of one saved revision: requests it, follows its
 * run and opens the marked PDF when it is ready (cached previews open at
 * once). Shared by the button and the actions menu.
 */
export const useTranslationPreview = (params: Params) => {
  const { documentId, revisionId, language, translations } = params;
  const labels = useDocumentCraftingEnumLabels();
  const { t } = useTranslation("documentCrafting");
  const [stored, setState] = useState<State>(() => idle(revisionId));
  // A new revision starts from scratch.
  const state = stored.revisionId === revisionId ? stored : idle(revisionId);
  const requestTranslation = useRequestDocumentCraftingTranslation();
  const run = useDocumentCraftingRun(state.runId ?? undefined);

  const runStatus = state.runId !== null ? run.data?.status : undefined;
  const runReady = runStatus === DocumentCraftingRunStatus.READY;
  const runFailed =
    runStatus === DocumentCraftingRunStatus.FAILED ||
    runStatus === DocumentCraftingRunStatus.CANCELLED;
  const working =
    requestTranslation.isPending ||
    (state.runId !== null && !isRunTerminal(runStatus) && !run.isError);
  const known = translations.find((item) => item.language === language);
  const ready = known?.status === DocumentCraftingTranslationStatus.READY;
  const pending =
    !working && known?.status === DocumentCraftingTranslationStatus.PENDING;

  const error = runFailed
    ? labels.errorCode(run.data?.error_code ?? null, run.data?.error)
    : state.error;

  const request = () => {
    if (ready && !runFailed) {
      setState({ ...idle(revisionId), open: true });
      return;
    }
    requestTranslation.mutate(
      { documentId, revisionId, language },
      {
        onSuccess: (response) => {
          if (response.run_id) {
            setState({ ...idle(revisionId), runId: response.run_id });
          } else if (
            response.translation.status === DocumentCraftingTranslationStatus.READY
          ) {
            setState({ ...idle(revisionId), open: true });
          } else {
            setState({
              ...idle(revisionId),
              error: t("detail.translation.failedGeneric"),
            });
          }
        },
      },
    );
  };

  return {
    ...params,
    ready,
    pending,
    working,
    progress: state.runId !== null ? (run.data?.progress ?? 0) : 0,
    error,
    // Opens by itself when the run the user started finishes.
    dialogOpen: state.open || runReady,
    request,
    closeDialog: () => setState(idle(revisionId)),
  };
};

export type TranslationPreview = ReturnType<typeof useTranslationPreview>;

/** "Preview in English/German" (machine translation) with its PDF viewer. */
const TranslationButton: React.FC<{ preview: TranslationPreview }> = ({
  preview,
}) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const [downloading, setDownloading] = useState(false);
  const language = labels.language(preview.language);

  const download = async () => {
    setDownloading(true);
    try {
      const response = await downloadDocumentCraftingTranslationPdf(
        preview.documentId,
        preview.revisionId,
        preview.language,
      );
      downloadDocumentBlob(
        response,
        downloadFileName(
          preview.title,
          preview.revisionNumber,
          "pdf",
          preview.language,
        ),
      );
    } catch {
      // The request shows the error snack.
    } finally {
      setDownloading(false);
    }
  };

  const label = preview.working
    ? t("detail.translation.preparing", {
        language,
        progress: preview.progress,
      })
    : preview.ready
      ? t("detail.translation.show", { language })
      : preview.pending
        ? t("detail.translation.pending", { language })
        : t("detail.translation.button", { language });

  return (
    <>
      <Tooltip title={t("detail.translation.hint")} arrow>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={
              preview.working ? (
                <CircularProgress size={14} />
              ) : (
                <TranslateRounded />
              )
            }
            onClick={preview.request}
            disabled={preview.working}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
      {preview.error && (
        <Box sx={{ flexBasis: "100%" }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={preview.request}>
                {t("common.retry")}
              </Button>
            }
          >
            {t("detail.translation.failed", { language, reason: preview.error })}
          </Alert>
        </Box>
      )}
      <PdfViewerDialog
        open={preview.dialogOpen}
        onClose={preview.closeDialog}
        url={documentCraftingFileUrls.translationPdf(
          preview.documentId,
          preview.revisionId,
          preview.language,
        )}
        title={t("detail.translation.dialogTitle", {
          language,
          number: preview.revisionNumber,
        })}
        notice={
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {t("detail.translation.marker")}
          </Alert>
        }
        onDownload={download}
        downloading={downloading}
      />
    </>
  );
};

export default TranslationButton;
